import { useEffect, useState } from "react";
import { authFetch } from "../../lib/authFetch";
import { argentinaDateKey } from "./towerShared";
import type { TowerDashboard } from "../../types/controlTower";

const MINUTE = 60_000;

export type LightdataSyncStatus = {
  // "unknown": la última consulta falló; no se sabe si sigue sincronizando.
  status: "ok" | "stale" | "never_synced" | "unknown" | string;
  lastSuccessfulSync: string | null;
};

// Corre `load` al montar, cada minuto mientras la pestaña está visible y al
// volver a ella. Una carga en curso no se repite; al desmontar se cancela.
function usePolling(load: (signal: AbortSignal) => Promise<void>, enabled: boolean, deps: unknown[]) {
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let inFlight = false;
    const run = async () => {
      if (inFlight || document.hidden) return;
      inFlight = true;
      try {
        await load(controller.signal);
      } finally {
        inFlight = false;
      }
    };
    run();
    const timer = window.setInterval(run, MINUTE);
    document.addEventListener("visibilitychange", run);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", run);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// Día argentino y reloj de la Torre: se actualizan cada minuto y al volver a
// la pestaña, así la jornada cambia sola a medianoche y la inactividad avanza.
export function useTowerClock() {
  const [date, setDate] = useState(() => argentinaDateKey());
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const tick = () => {
      setDate(argentinaDateKey());
      setNowMs(Date.now());
    };
    const timer = window.setInterval(tick, MINUTE);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);
  return { date, nowMs };
}

// Tablero del día de la Torre. `refresh` fuerza otra lectura (botón Reintentar).
export function useTowerDashboard(token: string | null, date: string, refresh: number) {
  const [data, setData] = useState<TowerDashboard | null>(null);
  const [error, setError] = useState("");
  usePolling(
    async (signal) => {
      // Pasada la medianoche no se vuelve a pedir el día que terminó: el reloj
      // cambia la fecha en el próximo minuto y se lee el día nuevo.
      if (argentinaDateKey() !== date) return;
      setError("");
      try {
        const query = new URLSearchParams({ date, shippingType: "all" });
        const res = await authFetch(`/v1/control-tower/drivers?${query}`, { token, signal });
        const payload = await res.json();
        if (!res.ok || !payload.ok) throw new Error(payload.error || "No se pudieron cargar los choferes.");
        if (!signal.aborted) setData(payload);
      } catch (err) {
        if (!signal.aborted) setError(err instanceof Error ? err.message : "No se pudieron cargar los choferes.");
      }
    },
    Boolean(token && date),
    [token, date, refresh],
  );
  return { data, error };
}

// Salud de la sincronización con Lightdata para el encabezado. Si no responde,
// la Torre sigue funcionando con la lectura de PostgreSQL.
export function useLightdataStatus(token: string | null, refresh: number) {
  const [status, setStatus] = useState<LightdataSyncStatus | null>(null);
  usePolling(
    async (signal) => {
      try {
        const res = await authFetch("/v1/lightdata/status", { token, signal });
        const payload = await res.json();
        if (!res.ok || !payload.ok) throw new Error("estado no disponible");
        if (!signal.aborted) setStatus(payload);
      } catch {
        // Sin respuesta, el último "OK" ya no vale: queda desconocido, con la
        // última sincronización exitosa conocida.
        if (!signal.aborted)
          setStatus((previous) => ({ status: "unknown", lastSuccessfulSync: previous?.lastSuccessfulSync ?? null }));
      }
    },
    Boolean(token),
    [token, refresh],
  );
  return status;
}
